import {useCallback, useState} from 'react';

import {Skeleton, Typography} from '@mui/material';
import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';

import {AppButton, ButtonContainer} from '@/app/shared-components';

import {
  GROUPS_DASHBOARD_BALANCES,
  SEGMENT_CONTROLS,
} from '../../models/constants';
import {
  ESplitBillBalanceMode,
  IGroupParticipant,
} from '../../models/interfaces';
import {useGroupsTrackers} from '../../trackers';
import GroupDashboardActivities from '../GroupDashboardActivities/GroupDashboardActivities';
import {GroupMembers} from '../GroupMembers/GroupMembers';
import {BalanceItem} from '../GroupsBalanceItem/GroupsBalanceItem';
import {SegmentedControl} from '../GroupsSegmentControls/GroupsSegmentControls';

interface GroupsDashboardGeneralProps {
  isLoadingGroup: boolean;
  groupEmoji: string;
  groupName: string;
  balancesItems: any;
  members: any[];
  handleShare: () => void;
  handleAddExpense: () => void;
  onBalanceClick: (type: ESplitBillBalanceMode) => void;
  groupCurrentParticipant: IGroupParticipant;
  ownerGroupParticipant: IGroupParticipant;
}

export const GroupsDashboardGeneral = ({
  isLoadingGroup,
  groupEmoji,
  groupName,
  balancesItems,
  members,
  handleShare,
  handleAddExpense,
  onBalanceClick,
  groupCurrentParticipant,
  ownerGroupParticipant,
}: GroupsDashboardGeneralProps) => {
  const {t} = useTranslation();
  const [activeSegment, setActiveSegment] = useState(SEGMENT_CONTROLS[0].id);

  const {
    trackSelectSplitsYouAreOwed,
    trackSelectSplitsYouOwe,
    trackSelectSplitsControl,
  } = useGroupsTrackers(
    groupCurrentParticipant?.groupId,
    ownerGroupParticipant?.modakUserId,
    groupCurrentParticipant?.modakUserId,
  );

  const handleOwedClick = useCallback(() => {
    trackSelectSplitsYouAreOwed();
    onBalanceClick(ESplitBillBalanceMode.OWED);
  }, [onBalanceClick, trackSelectSplitsYouAreOwed]);

  const handleOweClick = useCallback(() => {
    trackSelectSplitsYouOwe();
    onBalanceClick(ESplitBillBalanceMode.OWE);
  }, [onBalanceClick, trackSelectSplitsYouOwe]);

  const handleControlClick = useCallback(
    (controlId: string) => {
      trackSelectSplitsControl(controlId);
      setActiveSegment(controlId);
    },
    [trackSelectSplitsControl],
  );

  return (
    <>
      {/* Header with group name and emoji */}
      <Box className="groups-dashboard-header">
        <Box className="groups-dashboard-header-avatar">
          <Box className="groups-dashboard-header-avatar-content">
            {isLoadingGroup ? (
              <Skeleton
                variant="circular"
                width={40}
                height={40}
              />
            ) : (
              <Typography className="groups-dashboard-header-avatar-emoji">
                {groupEmoji}
              </Typography>
            )}
          </Box>
        </Box>
        {isLoadingGroup ? (
          <Skeleton
            variant="text"
            width={120}
            height={30}
          />
        ) : (
          <Typography className="groups-dashboard-header-name">
            {groupName}
          </Typography>
        )}
      </Box>
      {/* Segment control (Balances - Expenses) */}
      <SegmentedControl
        items={SEGMENT_CONTROLS}
        value={activeSegment}
        onChange={handleControlClick}
      />
      {/* Balances tab header content */}
      {activeSegment === SEGMENT_CONTROLS[0].id && (
        <>
          {/* Participant Balances area */}
          <Box className="groups-dashboard-balances">
            <BalanceItem
              id={GROUPS_DASHBOARD_BALANCES.OWED.id}
              image={GROUPS_DASHBOARD_BALANCES.OWED.image}
              imageAlt={GROUPS_DASHBOARD_BALANCES.OWED.imageAlt}
              title={balancesItems.owed.title}
              subtitle={balancesItems.owed.subtitle}
              isLoading={isLoadingGroup}
              onClick={handleOwedClick}
            />
            <BalanceItem
              id={GROUPS_DASHBOARD_BALANCES.OWE.id}
              image={GROUPS_DASHBOARD_BALANCES.OWE.image}
              imageAlt={GROUPS_DASHBOARD_BALANCES.OWE.imageAlt}
              title={balancesItems.owe.title}
              subtitle={balancesItems.owe.subtitle}
              isLoading={isLoadingGroup}
              onClick={handleOweClick}
            />
          </Box>
          {/* Members Area */}
          <GroupMembers
            members={members}
            onInvite={handleShare}
            isLoading={isLoadingGroup}
          />
        </>
      )}

      {/* Expenses tab header content */}
      {activeSegment === SEGMENT_CONTROLS[1].id && (
        <GroupDashboardActivities
          members={members}
          groupCurrentParticipant={groupCurrentParticipant}
          ownerGroupParticipant={ownerGroupParticipant}
        />
      )}

      <ButtonContainer sticky>
        {isLoadingGroup ? (
          <Skeleton
            variant="rectangular"
            height={48}
          />
        ) : (
          <AppButton
            id="group-dashboard-add-expense"
            label={t('groups.dashboard.add_expense')}
            onClick={handleAddExpense}
          />
        )}
      </ButtonContainer>
    </>
  );
};
